import { useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Player, PlayerEvent } from "@lottiefiles/react-lottie-player"
import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { USER_REPORTS_ROUTE } from "../../../../../constants/routes"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import failureAnimation from "../../../../login/assets/failure-lottie-anim.json"

import { TakeawayResultCard } from "./TakeawayResultCard"

type Props = Readonly<{
    title: string
    description: string
}>

const useStyles = makeStyles()((theme) => ({
    animation: {
        height: "4rem",
    },
    textBlock: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: ".25rem",
    },
    title: {
        fontWeight: 600,
        fontSize: "1.5rem",
        lineHeight: 1.33,
        color: theme.palette.tailwind.slate[950],
        textAlign: "center",
    },
    description: {
        fontWeight: 400,
        fontSize: ".875rem",
        lineHeight: 1.43,
        color: theme.palette.tailwind.slate[950],
        textAlign: "center",
    },
    button: {
        width: "100%",
        borderRadius: ".5rem",
        fontWeight: 600,
        marginTop: ".5rem",
    },
}))

export function TakeawayOrderFailed({ title, description }: Props) {
    const { classes } = useStyles()
    const playerRef = useRef<Player>(null)
    const navigate = useNavigate()

    const handleGoToDashboard = useCallback(() => navigate(USER_REPORTS_ROUTE), [navigate])

    return (
        <TakeawayResultCard>
            <Player
                ref={playerRef}
                src={failureAnimation}
                autoplay
                speed={1}
                keepLastFrame
                className={classes.animation}
                onEvent={(event) => {
                    if (event === PlayerEvent.Complete) playerRef.current?.pause()
                }}
            />

            <Box className={classes.textBlock}>
                <Typography className={classes.title}>{title}</Typography>
                <Typography className={classes.description}>{description}</Typography>
            </Box>

            <LocalizedStrict id="takeaway-payment-back-to-dashboard-button">
                <StandardButton
                    variant="contained"
                    disableElevation
                    className={classes.button}
                    onClick={handleGoToDashboard}
                >
                    Back to Dashboard
                </StandardButton>
            </LocalizedStrict>
        </TakeawayResultCard>
    )
}
